// React and MUI imports for hooks, layout, and UI
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, // MUI layout component
  Typography, // MUI text component
  Container, // MUI container for responsive width
  useMediaQuery, // MUI hook for responsive design
  useTheme, // MUI hook for theme access
  CssBaseline // MUI component to normalize CSS
} from '@mui/material';
import Header from '../../commons/Header'; // App header
import Footer from '../../commons/Footer'; // App footer
import Api from '../../Api'; // API utility for backend calls

/**
 * EmailActivation
 * Handles the email activation process after a user clicks the activation link in their email.
 * Verifies the code from the URL, shows success or error, and redirects as needed.
 */
function EmailActivation(props: any) {
  // Get URL search params (e.g., ?code=...&source=...)
  let [params, SetParams] = useSearchParams();
  // React Router navigation function
  let navigate = useNavigate();
  // Activation code from email link
  let code = params.get('code');
  // Optional source param for redirect
  let source = params.get('source');
  // Access the current MUI theme object
  const theme = useTheme();
  // Determine if the screen is mobile size
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // State for body content (error message, etc.)
  const [bodyContent, setBodyContent] = useState<any>('');

  // Inline style for error text
  const StyledText = {
    fontFamily: 'Avenir',
    fontStyle: 'normal',
    fontWeight: 300,
    fontSize: '16px',
    lineHeight: '24px',
    color: '#E449BD',
    marginTop: '15px',
  };

  // Inline style for the sign-in button link
  const StyledSignInBtn = {
    fontFamily: 'Aventa',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.mode == 'light' ? '#18768C' : '#2CDADA',
    padding: '0px',
    ':hover': {
      color: theme.palette.mode == 'light' ? '#18768C' : '#2CDADA',
    },
  };

  // On mount, verify the email activation code
  useEffect(() => {
    // Call backend API to verify the code
    Api.verifyEmail(code)
      .then((res) => {
        if (res.success) {
          // If successful, redirect to login (with source if present)
          if (source) {
            navigate(`/login/?source=${source}`);
          } else {
            navigate('/login');
          }
        } else {
          // If expired or invalid, show error and link to sign-up
          const link = <a href={window.location.origin + '/register'} style={StyledSignInBtn}>Return to sign-up screen</a>;
          const msg = <><p style={StyledText}>Your activation link has expired.<br /></p>{link}</>;
          setBodyContent(msg);
        }
      })
      .catch(() => {
        // On error, redirect to login
        navigate('/login');
      });
  }, []); // Run once on mount

  // Style for the title text
  const TitleStyle = {
    fontFamily: 'Aventa',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: isMobile ? '31px' : '41px',
    lineHeight: isMobile ? '41px' : '54px',
    color: theme.palette.mode == 'light' ? '#002136' : '#FFFFFF',
    textAlign: 'left',
  };

  // Style for the main vertical flex box
  const MainStyledBox = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  };

  // Style for the left divider (desktop only)
  const StyledDividerLeft = {
    borderLeft: isMobile
      ? ' '
      : theme.palette.mode == 'light'
        ? '1px solid rgba(0, 33, 54, 0.2)'
        : '1px solid  rgba(255, 255, 255, 0.2)',
    paddingLeft: isMobile ? ' ' : '40px',
    marginTop: isMobile ? '40px' : '140px',
    width: isMobile ? '100%' : '50%',
  };

  // Style for the main wrapper, with background image based on theme and device
  const StyledBoxMainWrapper = {
    background:
      theme.palette.mode == 'light'
        ? isMobile
          ? 'url(res/SignInBGLightMobile.svg) no-repeat top'
          : 'url(res/SignInBGLight.svg) no-repeat top'
        : isMobile
          ? 'url(res/SignInBGDarkMobile.svg) no-repeat top'
          : 'url(res/SignInBGDark.svg) no-repeat top',
    backgroundPosition: isMobile ? 'right bottom' : 'right',
    height: isMobile ? '510px' : '570px',
  };

  return (
    <>
      {/* Normalize CSS for consistent look */}
      <CssBaseline />
      {/* App header (logo, nav, etc.) */}
      <Header />
      {/* Main background section with theme-based image */}
      <Box sx={StyledBoxMainWrapper}>
        <Container maxWidth="lg">
          <Box sx={StyledDividerLeft}>
            <Box>
              {/* Main content: title and body (error or nothing) */}
              <Box sx={MainStyledBox}>
                <Typography variant="h2" sx={TitleStyle}>
                  Activating Email
                </Typography>
                {/* Show error message and sign-up link if activation fails */}
                {bodyContent}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      {/* App footer (copyright, links, etc.) */}
      <Footer />
    </>
  );
}

export default EmailActivation;
